<template>
  <div class="bg-gray-900 min-h-screen w-full">
    <div class="px-4 py-8 mx-auto container">
      <h1 class="text-2xl font-bold text-white mb-6">Pratim</h1>

      <!-- Tab navigation -->
      <FollowingTabs :active-tab="activeTab" @change-tab="handleTabChange" />

      <!-- SAMO AKTIVNI TAB SE RENDERUJE -->
      <!-- Products tab content -->
      <div v-if="activeTab === 'products'" class="w-full mt-6">
        <!-- Loader while data is fetching -->
        <div v-if="showLoader" class="py-12 flex justify-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
          ></div>
        </div>

        <!-- Error message -->
        <div v-else-if="error" class="w-full">
          <div class="bg-red-900 text-white p-4 rounded-lg mb-6">
            {{ error }}
          </div>
        </div>

        <div
          v-else-if="followedProducts.length === 0"
          class="text-center py-10 text-gray-400"
        >
          Ne pratite nijedan proizvod
        </div>

        <div v-else class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ProductCard
              v-for="product in followedProducts"
              :key="product.id"
              :product="product"
              @unfollow="handleProductUnfollow"
            />
          </div>

          <!-- Paginacija SAMO za proizvode -->
          <PaginationComponent
            v-if="productsMeta && productsMeta.totalPages > 1"
            :meta="productsMeta"
            :current-page="productsCurrentPage"
            :update-url="true"
            @page-change="handleProductsPageChange"
          />
        </div>
      </div>

      <!-- Users tab content -->
      <div v-if="activeTab === 'users'" class="w-full mt-6">
        <!-- Loader while data is fetching -->
        <div v-if="showLoader" class="py-12 flex justify-center">
          <div
            class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
          ></div>
        </div>

        <!-- Error message -->
        <div v-else-if="error" class="w-full">
          <div class="bg-red-900 text-white p-4 rounded-lg mb-6">
            {{ error }}
          </div>
        </div>

        <div
          v-else-if="followedUsers.length === 0"
          class="text-center py-10 text-gray-400"
        >
          Ne pratite nijednog korisnika
        </div>

        <div v-else class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <UserCard
              v-for="user in followedUsers"
              :key="user.id"
              :user="user"
              @unfollow="handleUserUnfollow"
            />
          </div>

          <!-- Paginacija SAMO za korisnike -->
          <PaginationComponent
            v-if="usersMeta && usersMeta.totalPages > 1"
            :meta="usersMeta"
            :current-page="usersCurrentPage"
            :update-url="true"
            @page-change="handleUsersPageChange"
          />
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import '@/styles/follow-tabs-fix.css';
import {
  defineComponent,
  ref,
  onMounted,
  watch,
  computed,
  onBeforeUnmount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import ProductCard from '@/components/ProductCard.vue';
import UserCard from '@/components/UserCard.vue';
import FollowingTabs from '@/components/following/FollowingTabs.vue';
import ToastNotification from '@/components/ToastNotification.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import { debounce } from '@/utils/debounce';

// Create/update these composables with appropriate functionality
import { useFollowedProducts } from '@/composables/useFollowedProducts';
import { useFollowedUsers } from '@/composables/useFollowedUsers';

export default defineComponent({
  name: 'FollowingView',
  components: {
    ProductCard,
    UserCard,
    FollowingTabs,
    ToastNotification,
    PaginationComponent,
  },
  setup() {
    // Get current route and router
    const route = useRoute();
    const router = useRouter();

    // Get active tab from URL query or default to 'products'
    const activeTab = ref((route.query.tab as string) || 'products');

    // Debounced loader state
    const showLoader = ref(false);

    // Debounced functions for loader
    const showLoaderFn = () => {
      showLoader.value = true;
    };

    const hideLoaderFn = () => {
      showLoader.value = false;
    };

    // Create debounced function with 500ms delay
    const debouncedShowLoader = debounce(showLoaderFn, 500);

    // Immediate hide loader (with no delay)
    const immediateHideLoader = () => {
      // Cancel the show loader timeout if it exists
      debouncedShowLoader.cancel();
      hideLoaderFn();
    };

    // Get current pages from URL query
    const productsCurrentPage = computed(() => {
      if (activeTab.value !== 'products') return 1;
      const page = parseInt(route.query.page as string, 10);
      return isNaN(page) || page < 1 ? 1 : page;
    });

    const usersCurrentPage = computed(() => {
      if (activeTab.value !== 'users') return 1;
      const page = parseInt(route.query.page as string, 10);
      return isNaN(page) || page < 1 ? 1 : page;
    });

    // Use composables for products and users
    const {
      products: followedProducts,
      meta: productsMeta,
      loading: productsLoading,
      error: productsError,
      fetch: fetchProducts,
      unfollowWithPagination: unfollowProductWithPagination,
      toast: productsToast,
    } = useFollowedProducts();

    const {
      users: followedUsers,
      meta: usersMeta,
      loading: usersLoading,
      error: usersError,
      fetch: fetchUsers,
      unfollowWithPagination: unfollowUserWithPagination,
      toast: usersToast,
    } = useFollowedUsers();

    // Combined error state
    const error = ref<string | null>(null);

    // Combined toast
    const toast = ref({
      show: false,
      message: '',
      type: 'info' as 'info' | 'success' | 'warning' | 'error',
    });

    // Watch for changes in loading states and apply debounce
    watch(
      [productsLoading, usersLoading],
      ([newProductsLoading, newUsersLoading]) => {
        const isLoading =
          activeTab.value === 'products' ? newProductsLoading : newUsersLoading;

        if (isLoading) {
          // Only show loader if loading takes longer than the debounce timeout
          debouncedShowLoader();
        } else {
          immediateHideLoader();
        }
      }
    );

    watch([productsError, usersError], ([newProductsError, newUsersError]) => {
      error.value =
        activeTab.value === 'products' ? newProductsError : newUsersError;
    });

    // Watch for toast updates
    watch([productsToast, usersToast], ([newProductsToast, newUsersToast]) => {
      if (newProductsToast.show) {
        toast.value = newProductsToast;
      } else if (newUsersToast.show) {
        toast.value = newUsersToast;
      }
    });

    // Watch for route changes (page parameter)
    watch(
      () => route.query.page,
      (newPage) => {
        if (activeTab.value === 'products') {
          const page = parseInt(newPage as string, 10) || 1;
          fetchProducts(page);
        } else if (activeTab.value === 'users') {
          const page = parseInt(newPage as string, 10) || 1;
          fetchUsers(page);
        }
      }
    );

    // Watch for tab changes in URL
    watch(
      () => route.query.tab,
      (newTab) => {
        if (newTab && newTab !== activeTab.value) {
          activeTab.value = newTab as string;
          handleTabChange(newTab as string);
        }
      }
    );

    const handleTabChange = (tab: string) => {
      activeTab.value = tab;

      // Update URL with new tab and reset page to 1
      router.push({
        path: route.path,
        query: { tab, page: '1' },
      });

      if (tab === 'products') {
        fetchProducts(1);
        error.value = productsError.value;
      } else if (tab === 'users') {
        fetchUsers(1);
        error.value = usersError.value;
      }
    };

    // Pagination handlers - these will be called by PaginationComponent when updateUrl is true
    const handleProductsPageChange = (page: number) => {
      console.log('handleProductsPageChange called with page:', page);
      // PaginationComponent will handle URL update since updateUrl is true
    };

    const handleUsersPageChange = (page: number) => {
      console.log('handleUsersPageChange called with page:', page);
      // PaginationComponent will handle URL update since updateUrl is true
    };

    const handleProductUnfollow = async (productId: string) => {
      await unfollowProductWithPagination(
        productId,
        productsCurrentPage.value,
        (newPage) => {
          // Update URL when page changes due to unfollow
          router.push({
            path: route.path,
            query: { ...route.query, page: newPage.toString() },
          });
        }
      );
    };

    const handleUserUnfollow = async (userId: string) => {
      await unfollowUserWithPagination(
        userId,
        usersCurrentPage.value,
        (newPage) => {
          // Update URL when page changes due to unfollow
          router.push({
            path: route.path,
            query: { ...route.query, page: newPage.toString() },
          });
        }
      );
    };

    onMounted(() => {
      // Initial fetch based on current tab and page from URL
      if (activeTab.value === 'products') {
        fetchProducts(productsCurrentPage.value);
      } else if (activeTab.value === 'users') {
        fetchUsers(usersCurrentPage.value);
      }
    });

    // Clean up on unmount
    onBeforeUnmount(() => {
      debouncedShowLoader.cancel();
    });

    return {
      activeTab,
      showLoader,
      error,
      followedProducts,
      followedUsers,
      productsCurrentPage,
      usersCurrentPage,
      productsMeta,
      usersMeta,
      toast,
      handleTabChange,
      handleProductsPageChange,
      handleUsersPageChange,
      handleProductUnfollow,
      handleUserUnfollow,
    };
  },
});
</script>
